/**
 * Sauvegardes (A6) — SIMULATION : export, empreinte SHA-256, restauration à blanc, restauration avec procès-verbal.
 * ⚠️ Ne constitue pas une sauvegarde de production (voir docs/MYRON_PLAN_DEPLOIEMENT.md §2).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Table, TextField, useToast } from '@myron/design-system';
import { DatabaseBackup, RotateCcw, ShieldCheck } from 'lucide-react';
import { get, post } from '../../../shared/api/client';
import { EntetePage, ErreurFormulaire, EtatRequete } from '../../../shared/ui/composants';
import { dateHeure } from '../../../shared/ui/format';
import { useSession } from '../../../shared/auth/session';

interface Sauvegarde {
  id: string; reference: string; creeLe: string; creePar: string; empreinte: string; tailleOctets: number;
  compteurs: Record<string, number>; tablesExclues: string[]; audit: { nbEntrees: number; chaineIntegre: boolean };
}
interface Verification {
  reference: string; mode: 'A_BLANC' | 'APRES_RESTAURATION'; verifieLe: string; empreinteConforme: boolean; compteursConformes: boolean;
  ecartsCompteurs: { table: string; attendu: number; obtenu: number }[]; chaineAuditIntegre: boolean; nbEntreesAuditVerifiees: number;
  secretsAbsentsDeLArchive: boolean; conforme: boolean; empreinteCalculee: string; lignesRestaurees?: number; restaurePar?: string;
}

const ko = (n: number) => `${(n / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Ko`;
const total = (c: Record<string, number>) => Object.values(c).reduce((a, b) => a + b, 0);

function Controle({ ok, libelle }: { ok: boolean; libelle: string }) {
  return <li><Badge ton={ok ? 'succes' : 'erreur'}>{ok ? 'Conforme' : 'Non conforme'}</Badge> {libelle}</li>;
}

function ProcesVerbal({ v }: { v: Verification }) {
  return (
    <Card as="section" className="my-pile" aria-labelledby="titre-pv" data-testid="proces-verbal">
      <h2 id="titre-pv" className="my-titre-4">{v.mode === 'A_BLANC' ? 'Restauration à blanc' : 'Procès-verbal de restauration'} — {v.reference}</h2>
      <Alert ton={v.conforme ? 'succes' : 'erreur'} titre={v.conforme ? 'Vérification conforme' : 'Vérification NON conforme'}>
        {dateHeure(v.verifieLe)}{v.restaurePar ? ` · restaurée par ${v.restaurePar} · ${v.lignesRestaurees} lignes` : ' · aucune donnée modifiée'}
      </Alert>
      <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0 }}>
        <Controle ok={v.empreinteConforme} libelle="Empreinte SHA-256 identique" />
        <Controle ok={v.compteursConformes} libelle={v.compteursConformes ? 'Nombre de lignes par table identique' : `Écarts : ${v.ecartsCompteurs.map((e) => `${e.table} ${e.attendu}→${e.obtenu}`).join(', ')}`} />
        <Controle ok={v.chaineAuditIntegre} libelle={`Chaîne d’audit intègre (${v.nbEntreesAuditVerifiees} entrées vérifiées)`} />
        <Controle ok={v.secretsAbsentsDeLArchive} libelle="Aucun secret en clair dans l’archive" />
      </ul>
      <p className="my-legende my-secondaire">Empreinte calculée : <code>{v.empreinteCalculee.slice(0, 16)}…</code></p>
    </Card>
  );
}

export function PageSauvegardes() {
  const qc = useQueryClient();
  const toast = useToast();
  const { peut } = useSession();
  const superAdmin = peut('sauvegarde.gerer');
  const r = useQuery({ queryKey: ['sauvegardes'], queryFn: () => get<{ donnees: Sauvegarde[] }>('/admin/sauvegardes') });
  const [pv, setPv] = useState<Verification | null>(null);
  const [aRestaurer, setARestaurer] = useState<Sauvegarde | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const creer = useMutation({
    mutationFn: () => post<Sauvegarde>('/admin/sauvegardes'),
    onSuccess: (s) => { void qc.invalidateQueries({ queryKey: ['sauvegardes'] }); toast({ ton: 'succes', titre: `Sauvegarde ${s.reference} créée` }); },
  });
  const verifier = useMutation({
    mutationFn: (id: string) => post<Verification>(`/admin/sauvegardes/${id}/verifier`),
    onSuccess: (v) => setPv(v),
  });
  const restaurer = useMutation({
    mutationFn: (s: Sauvegarde) => post<{ procesVerbal: Verification }>(`/admin/sauvegardes/${s.id}/restaurer`, { confirmation }),
    onSuccess: (res) => { setPv(res.procesVerbal); setARestaurer(null); setConfirmation(''); void qc.invalidateQueries(); toast({ ton: res.procesVerbal.conforme ? 'succes' : 'erreur', titre: 'Restauration terminée' }); },
  });
  return (
    <>
      <EntetePage titre="Sauvegardes" sousTitre="Export des données, empreinte, restauration à blanc et restauration vérifiée."
        actions={superAdmin ? <Button icone={DatabaseBackup} chargement={creer.isPending} onClick={() => creer.mutate()}>Créer une sauvegarde</Button> : undefined} />
      <Alert ton="alerte" titre="SIMULÉ — ne constitue pas une sauvegarde de production">
        L’archive reste dans ce navigateur, sans chiffrement ni copie hors site. Elle illustre le protocole exigé en production (archive chiffrée hors serveur, test de restauration consigné). Mots de passe, secrets MFA et IBAN sont exclus de l’archive ; sessions et jetons ne sont ni sauvegardés ni restaurés.
      </Alert>
      <ErreurFormulaire erreur={creer.error ?? verifier.error} />
      <div style={{ marginTop: 16 }}>
        <EtatRequete requete={r} vide={(d) => !d.donnees.length} titreVide="Aucune sauvegarde" texteVide={superAdmin ? 'Créez une première sauvegarde pour pouvoir tester la restauration.' : 'Aucune sauvegarde n’a encore été créée par un super-administrateur.'} enfants={(d) => (
          <Table<Sauvegarde> legende="Sauvegardes" lignes={d.donnees} cle={(s) => s.id} colonnes={[
            { cle: 'r', titre: 'Référence', rendu: (s) => <strong>{s.reference}</strong> },
            { cle: 'd', titre: 'Créée', rendu: (s) => <>{dateHeure(s.creeLe)}<br /><span className="my-legende my-secondaire">{s.creePar}</span></> },
            { cle: 'c', titre: 'Contenu', rendu: (s) => <>{total(s.compteurs).toLocaleString('fr-FR')} lignes · {Object.keys(s.compteurs).length} tables<br /><span className="my-legende my-secondaire">{ko(s.tailleOctets)}</span></> },
            { cle: 'e', titre: 'Empreinte', rendu: (s) => <code title={s.empreinte}>{s.empreinte.slice(0, 12)}…</code> },
            { cle: 'a', titre: 'Audit', rendu: (s) => <Badge ton={s.audit.chaineIntegre ? 'succes' : 'erreur'}>{`${s.audit.nbEntrees} entrées`}</Badge> },
            { cle: 'x', titre: 'Actions', rendu: (s) => (
              <div className="my-ligne">
                <Button variante="secondaire" compact icone={ShieldCheck} chargement={verifier.isPending && verifier.variables === s.id} onClick={() => verifier.mutate(s.id)}>Restauration à blanc</Button>
                {superAdmin && <Button variante="danger" compact icone={RotateCcw} onClick={() => { setARestaurer(s); setConfirmation(''); restaurer.reset(); }}>Restaurer…</Button>}
              </div>
            ) },
          ]} />
        )} />
      </div>
      {aRestaurer && (
        <Card as="section" className="my-pile" aria-labelledby="titre-restaurer" style={{ marginTop: 16 }}>
          <h2 id="titre-restaurer" className="my-titre-4">Restaurer {aRestaurer.reference}</h2>
          <p className="my-petit">Toutes les données (hors sessions, jetons et secrets) seront remplacées par l’état du {dateHeure(aRestaurer.creeLe)}. Saisissez <strong>RESTAURER {aRestaurer.reference}</strong> pour confirmer.</p>
          <TextField label="Confirmation" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="off" />
          <ErreurFormulaire erreur={restaurer.error} />
          <div className="my-ligne">
            <Button variante="tertiaire" onClick={() => setARestaurer(null)}>Annuler</Button>
            <Button variante="danger" icone={RotateCcw} disabled={confirmation !== `RESTAURER ${aRestaurer.reference}`} chargement={restaurer.isPending} onClick={() => restaurer.mutate(aRestaurer)}>Restaurer et vérifier</Button>
          </div>
        </Card>
      )}
      {pv && <div style={{ marginTop: 16 }}><ProcesVerbal v={pv} /></div>}
    </>
  );
}
